/**
 * https://usehooks.com/useLocalStorage/
 */

import { useState } from 'react';
import { useLogger } from '../../appLogger';

export function useLocalStorage<T>(key: string, initialValue: T) {
  const appLogger = useLogger();
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error: any) {
      // If error also return initialValue
      appLogger.error(error, 'Local storage create state: ' + error?.message);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error: any) {
      // A more advanced implementation would handle the error case
      appLogger.error(error, 'Local storage set value: ' + error?.message);
    }
  };

  return [storedValue, setValue] as [T, (v: T) => void];
}
